// Frontend fallback taxonomy for talent-pool intelligence.
// The backend owns the canonical parse, but this keeps older profiles readable
// until they are reprocessed onto the latest schema.

const POSITION_FAMILY_RULES = {
  Engineering: ['engineer', 'developer', 'devops', 'platform', 'qa', 'quality assurance', 'software', 'frontend', 'backend', 'full stack', 'mobile', 'sre', 'security engineer'],
  Product: ['product manager', 'product owner', 'program manager', 'project manager', 'scrum master'],
  Data: ['data analyst', 'data scientist', 'analytics', 'machine learning', 'data engineer', 'bi analyst', 'business intelligence'],
  Design: ['designer', 'ux', 'ui', 'product design', 'graphic', 'visual design'],
  Marketing: ['marketing', 'growth', 'seo', 'sem', 'content', 'brand', 'demand generation'],
  Sales: ['sales', 'account executive', 'business development', 'partnership', 'customer success', 'account manager', 'sdr', 'bdr'],
  Finance: ['finance', 'accountant', 'controller', 'auditor', 'financial analyst'],
  People: ['recruiter', 'talent acquisition', 'human resources', 'people operations', 'hr', 'sourcer'],
  Operations: ['operations', 'supply chain', 'logistics', 'procurement', 'business operations'],
  'IT & Security': ['it support', 'system administrator', 'cybersecurity', 'security analyst', 'information security'],
  'Legal & Compliance': ['legal', 'counsel', 'compliance', 'attorney', 'lawyer'],
  Executive: ['chief ', 'ceo', 'cto', 'coo', 'cfo', 'cmo', 'vp ', 'vice president', 'head of', 'director']
};

const INDUSTRY_RULES = {
  'Software & SaaS': ['saas', 'software', 'cloud software', 'enterprise software', 'platform'],
  Fintech: ['fintech', 'payments', 'payment', 'insurtech', 'wealthtech', 'neobank'],
  'Banking & Financial Services': ['banking', 'financial services', 'investment', 'asset management', 'capital markets', 'insurance'],
  'Healthcare & Healthtech': ['healthcare', 'health care', 'healthtech', 'hospital', 'clinical', 'medtech', 'medical'],
  'Life Sciences & Biotech': ['biotech', 'biotechnology', 'pharma', 'pharmaceutical', 'life sciences'],
  'E-commerce & Retail': ['ecommerce', 'e commerce', 'retail', 'marketplace'],
  'Consumer Goods': ['fmcg', 'consumer goods', 'cpg'],
  'Manufacturing & Industrial': ['manufacturing', 'industrial', 'factory', 'production'],
  'Logistics & Supply Chain': ['logistics', 'supply chain', 'freight', 'warehousing'],
  Telecommunications: ['telecom', 'telecommunications', 'wireless'],
  'Media & Entertainment': ['media', 'entertainment', 'publishing', 'gaming', 'streaming'],
  'Marketing & Advertising': ['advertising', 'adtech', 'marketing agency', 'creative agency'],
  'Education & Edtech': ['education', 'edtech', 'school', 'learning', 'university'],
  'Government & Public Sector': ['government', 'public sector', 'municipal', 'federal', 'state agency'],
  'Consulting & Professional Services': ['consulting', 'professional services', 'agency', 'advisory'],
  'Real Estate & Construction': ['real estate', 'proptech', 'construction', 'property'],
  'Energy & Utilities': ['energy', 'utilities', 'oil', 'gas', 'renewable', 'solar', 'power'],
  'Travel & Hospitality': ['travel', 'hospitality', 'hotel', 'tourism', 'airline'],
  'Automotive & Mobility': ['automotive', 'mobility', 'transportation', 'vehicle', 'ev'],
  'Security & Defense': ['defense', 'defence', 'security', 'aerospace', 'military'],
  'Nonprofit & Social Impact': ['nonprofit', 'non profit', 'ngo', 'charity', 'social impact']
};

const DEPARTMENT_RULES = {
  Engineering: ['engineering', 'software', 'platform', 'development', 'qa', 'devops'],
  Product: ['product', 'program management', 'project management'],
  'Data & Analytics': ['data', 'analytics', 'business intelligence', 'machine learning'],
  Design: ['design', 'ux', 'ui', 'creative'],
  Marketing: ['marketing', 'growth', 'brand', 'content', 'demand generation'],
  Sales: ['sales', 'business development', 'account management'],
  'Customer Success': ['customer success', 'client services', 'customer experience'],
  Finance: ['finance', 'accounting', 'fp&a', 'audit'],
  'People & HR': ['people', 'human resources', 'hr', 'talent acquisition', 'recruiting'],
  Operations: ['operations', 'business operations', 'supply chain', 'procurement', 'logistics'],
  'IT & Security': ['it', 'information technology', 'security', 'cybersecurity', 'infrastructure'],
  'Legal & Compliance': ['legal', 'compliance', 'regulatory'],
  Executive: ['executive', 'leadership', 'c suite', 'c-suite']
};

const ROLE_TITLE_RULES = {
  'Engineering Manager': ['engineering manager'],
  'Software Engineer': ['software engineer', 'software developer', 'developer'],
  'Frontend Engineer': ['frontend engineer', 'front end engineer', 'frontend developer', 'ui engineer', 'react developer'],
  'Backend Engineer': ['backend engineer', 'back end engineer', 'backend developer', 'api engineer'],
  'Full Stack Engineer': ['full stack engineer', 'full stack developer', 'fullstack engineer', 'fullstack developer'],
  'DevOps Engineer': ['devops engineer', 'site reliability', 'sre', 'platform engineer', 'infrastructure engineer'],
  'QA Engineer': ['qa engineer', 'quality assurance', 'test engineer', 'automation tester'],
  'Mobile Engineer': ['mobile engineer', 'mobile developer', 'ios developer', 'android developer', 'ios engineer', 'android engineer'],
  'Data Engineer': ['data engineer', 'etl engineer', 'analytics engineer'],
  'Data Analyst': ['data analyst', 'business analyst', 'bi analyst', 'business intelligence analyst'],
  'Data Scientist': ['data scientist'],
  'Machine Learning Engineer': ['machine learning engineer', 'ml engineer', 'ai engineer'],
  'Product Manager': ['product manager', 'product owner'],
  'Program Manager': ['program manager'],
  'Project Manager': ['project manager'],
  'Product Designer': ['product designer', 'ux designer', 'ui designer', 'ux ui designer', 'ui ux designer'],
  'Graphic Designer': ['graphic designer', 'visual designer', 'brand designer'],
  'Growth Marketer': ['growth marketer', 'growth marketing', 'demand generation'],
  'Digital Marketer': ['digital marketer', 'performance marketer', 'seo', 'sem'],
  'Content Marketer': ['content marketer', 'content manager', 'copywriter'],
  'Brand Marketer': ['brand marketer', 'brand manager'],
  'Marketing Manager': ['marketing manager', 'marketing lead'],
  'Account Executive': ['account executive'],
  'Sales Development Representative': ['sales development representative', 'business development representative', 'sdr', 'bdr'],
  'Sales Manager': ['sales manager'],
  'Business Development Manager': ['business development manager', 'partnerships manager', 'partner manager'],
  'Customer Success Manager': ['customer success manager', 'customer success', 'client success manager'],
  Recruiter: ['recruiter', 'talent acquisition specialist', 'talent acquisition', 'sourcer', 'technical recruiter'],
  'HR Generalist': ['hr generalist', 'human resources generalist', 'people partner'],
  'People Operations Manager': ['people operations manager', 'people operations', 'people ops', 'hr manager'],
  'Finance Manager': ['finance manager', 'fp&a manager'],
  'Financial Analyst': ['financial analyst'],
  Accountant: ['accountant', 'controller', 'auditor', 'bookkeeper'],
  'Operations Manager': ['operations manager', 'business operations manager', 'ops manager'],
  'Supply Chain Specialist': ['supply chain', 'logistics', 'procurement'],
  'IT Support Specialist': ['it support', 'help desk', 'helpdesk', 'system administrator'],
  'Security Engineer': ['security engineer', 'security analyst', 'cybersecurity engineer'],
  'Legal Counsel': ['legal counsel', 'attorney', 'lawyer', 'compliance counsel'],
  'Executive Leader': ['chief ', 'ceo', 'cto', 'coo', 'cfo', 'cmo', 'vp ', 'vice president', 'head of', 'director']
};

const ROLE_TO_FAMILY = {
  'Engineering Manager': 'Engineering',
  'Software Engineer': 'Engineering',
  'Frontend Engineer': 'Engineering',
  'Backend Engineer': 'Engineering',
  'Full Stack Engineer': 'Engineering',
  'DevOps Engineer': 'Engineering',
  'QA Engineer': 'Engineering',
  'Mobile Engineer': 'Engineering',
  'Data Engineer': 'Data',
  'Data Analyst': 'Data',
  'Data Scientist': 'Data',
  'Machine Learning Engineer': 'Data',
  'Product Manager': 'Product',
  'Program Manager': 'Product',
  'Project Manager': 'Product',
  'Product Designer': 'Design',
  'Graphic Designer': 'Design',
  'Growth Marketer': 'Marketing',
  'Digital Marketer': 'Marketing',
  'Content Marketer': 'Marketing',
  'Brand Marketer': 'Marketing',
  'Marketing Manager': 'Marketing',
  'Account Executive': 'Sales',
  'Sales Development Representative': 'Sales',
  'Sales Manager': 'Sales',
  'Business Development Manager': 'Sales',
  'Customer Success Manager': 'Sales',
  Recruiter: 'People',
  'HR Generalist': 'People',
  'People Operations Manager': 'People',
  'Finance Manager': 'Finance',
  'Financial Analyst': 'Finance',
  Accountant: 'Finance',
  'Operations Manager': 'Operations',
  'Supply Chain Specialist': 'Operations',
  'IT Support Specialist': 'IT & Security',
  'Security Engineer': 'IT & Security',
  'Legal Counsel': 'Legal & Compliance',
  'Executive Leader': 'Executive'
};

const FAMILY_DEFAULT_ROLES = {
  Engineering: 'Software Engineer',
  Product: 'Product Manager',
  Data: 'Data Analyst',
  Design: 'Product Designer',
  Marketing: 'Marketing Manager',
  Sales: 'Account Executive',
  Finance: 'Financial Analyst',
  People: 'Recruiter',
  Operations: 'Operations Manager',
  'IT & Security': 'IT Support Specialist',
  'Legal & Compliance': 'Legal Counsel',
  Executive: 'Executive Leader'
};

const FAMILY_ADJACENT_ROLES = {
  Engineering: ['Software Engineer', 'Frontend Engineer', 'Backend Engineer', 'Full Stack Engineer', 'DevOps Engineer', 'QA Engineer', 'Engineering Manager'],
  Product: ['Product Manager', 'Program Manager', 'Project Manager'],
  Data: ['Data Analyst', 'Data Scientist', 'Data Engineer', 'Machine Learning Engineer'],
  Design: ['Product Designer', 'Graphic Designer'],
  Marketing: ['Marketing Manager', 'Growth Marketer', 'Digital Marketer', 'Content Marketer', 'Brand Marketer'],
  Sales: ['Account Executive', 'Sales Development Representative', 'Business Development Manager', 'Customer Success Manager', 'Sales Manager'],
  Finance: ['Financial Analyst', 'Finance Manager', 'Accountant'],
  People: ['Recruiter', 'HR Generalist', 'People Operations Manager'],
  Operations: ['Operations Manager', 'Supply Chain Specialist'],
  'IT & Security': ['IT Support Specialist', 'Security Engineer'],
  'Legal & Compliance': ['Legal Counsel'],
  Executive: ['Executive Leader']
};

const FAMILY_TO_DEPARTMENT = {
  Engineering: 'Engineering',
  Product: 'Product',
  Data: 'Data & Analytics',
  Design: 'Design',
  Marketing: 'Marketing',
  Sales: 'Sales',
  Finance: 'Finance',
  People: 'People & HR',
  Operations: 'Operations',
  'IT & Security': 'IT & Security',
  'Legal & Compliance': 'Legal & Compliance',
  Executive: 'Executive'
};

const normalizeKey = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const cleanLabel = (value) => String(value || '').replace(/\s+/g, ' ').trim();

export const uniqueLabels = (values) => {
  const seen = new Set();
  const ordered = [];
  (values || []).forEach((value) => {
    const clean = cleanLabel(value);
    if (!clean) return;
    const key = clean.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    ordered.push(clean);
  });
  return ordered;
};

const matchRule = (value, rules) => {
  const normalized = normalizeKey(value);
  if (!normalized) return '';

  return Object.entries(rules).find(([canonical, keywords]) => (
    normalized === normalizeKey(canonical)
    || keywords.some((keyword) => normalized.includes(normalizeKey(keyword)))
  ))?.[0] || '';
};

export const canonicalizeRoleTitle = (value, { positionFamily = '', strict = false } = {}) => {
  const cleaned = cleanLabel(value);
  if (!cleaned) return strict ? '' : (FAMILY_DEFAULT_ROLES[positionFamily] || '');

  const matched = matchRule(cleaned, ROLE_TITLE_RULES);
  if (matched) return matched;
  if (positionFamily && !strict) return FAMILY_DEFAULT_ROLES[positionFamily] || cleaned;
  return strict ? '' : cleaned;
};

export const canonicalizePositionFamily = (title, industries = []) => {
  const matchedRole = canonicalizeRoleTitle(title, { strict: true });
  if (matchedRole && ROLE_TO_FAMILY[matchedRole]) return ROLE_TO_FAMILY[matchedRole];
  return matchRule(title, POSITION_FAMILY_RULES) || (industries.length ? 'General' : 'General');
};

export const canonicalizeIndustry = (value) => matchRule(value, INDUSTRY_RULES);

export const canonicalizeIndustries = (values) => uniqueLabels((values || []).map((value) => canonicalizeIndustry(value)).filter(Boolean));

export const canonicalizeDepartment = (rawDepartment, { positionFamily = '', title = '', skills = [] } = {}) => (
  matchRule(rawDepartment, DEPARTMENT_RULES)
  || FAMILY_TO_DEPARTMENT[positionFamily]
  || matchRule(title, DEPARTMENT_RULES)
  || matchRule((skills || []).join(' '), DEPARTMENT_RULES)
  || ''
);

export const canonicalizeRoleList = (values, { positionFamily = '', fallback = [], limit = 6 } = {}) => (
  uniqueLabels([
    ...(values || []).map((value) => canonicalizeRoleTitle(value, { positionFamily })),
    ...fallback
  ]).slice(0, limit)
);

export const canonicalizeAdjacentRoles = (values, { positionFamily = '', primaryRole = '', limit = 6 } = {}) => {
  const canonicalPrimary = canonicalizeRoleTitle(primaryRole, { positionFamily });
  return canonicalizeRoleList(values, {
    positionFamily,
    fallback: FAMILY_ADJACENT_ROLES[positionFamily] || [],
    limit: limit + 1
  }).filter((value) => value && value !== canonicalPrimary).slice(0, limit);
};

export const canonicalizeLeadershipReadiness = (
  rawValue,
  {
    seniorityBucket = '',
    title = '',
    years = 0,
    peopleManagementScope = '',
    leadershipScope = ''
  } = {}
) => {
  const normalized = normalizeKey([
    rawValue,
    seniorityBucket,
    title,
    peopleManagementScope,
    leadershipScope
  ].join(' '));

  if (['chief', 'vp', 'vice president', 'executive', 'cfo', 'cto', 'ceo', 'coo'].some((token) => normalized.includes(token))) {
    return 'Executive';
  }
  if (['director', 'head of', 'senior leadership'].some((token) => normalized.includes(token))) {
    return 'Senior Leadership';
  }
  if (['manager', 'people manager', 'managed', 'team of', 'hired', 'budget ownership'].some((token) => normalized.includes(token))) {
    return 'People Manager';
  }
  if (['lead', 'principal', 'staff'].some((token) => normalized.includes(token))) {
    return 'Team Lead';
  }
  if (Number(years) > 0 || title || seniorityBucket) {
    return 'Individual Contributor';
  }
  return 'Unspecified';
};
